import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MarkEmailReadRoundedIcon from '@mui/icons-material/MarkEmailReadRounded';
import { authService } from '../api/auth';
import { colors } from '../../../theme';

/** Seconds the resend button stays disabled after a send. */
const RESEND_COOLDOWN_SECONDS = 45;

/**
 * Post-registration email confirmation (TCH-02, story S2.2).
 *
 * Reachable two ways:
 *   1. Redirect from Register, carrying the address in router state.
 *   2. The link in the verification email: /verify-email?email=…&code=…,
 *      which verifies on arrival without the user typing anything.
 *
 * Confirmation goes through `POST /auth/confirm-registration` rather than
 * straight to Cognito, because that endpoint also flips the DynamoDB user
 * record to VERIFIED.
 */
const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const stateEmail = (location.state as { email?: string } | null)?.email;
  const queryEmail = searchParams.get('email');
  const queryCode = searchParams.get('code');

  const [email, setEmail] = useState(stateEmail || queryEmail || '');
  const [code, setCode] = useState(queryCode || '');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const autoSubmitted = useRef(false);

  // Resend cooldown tick
  useEffect(() => {
    if (cooldown <= 0) return;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const verify = useCallback(
    async (emailValue: string, codeValue: string) => {
      setError('');
      setNotice('');

      if (!emailValue.trim()) {
        setError('Ingresa el correo con el que te registraste.');
        return;
      }
      if (!/^\d{6}$/.test(codeValue.trim())) {
        setError('El código son 6 dígitos. Revísalo e inténtalo de nuevo.');
        return;
      }

      setVerifying(true);
      try {
        await authService.verifyEmail({ email: emailValue.trim(), code: codeValue.trim() });
        navigate('/login', {
          replace: true,
          state: {
            verified: true,
            email: emailValue.trim(),
          },
        });
      } catch (err: any) {
        const message: string = err?.message || '';
        if (/already.*confirm|CONFIRMED/i.test(message)) {
          // Not an error worth blocking on — send them to sign in.
          navigate('/login', {
            replace: true,
            state: { verified: true, email: emailValue.trim() },
          });
          return;
        }
        if (/expired/i.test(message)) {
          setError('Ese código ya venció. Pide uno nuevo con el botón de abajo.');
        } else if (/mismatch|invalid|not match/i.test(message)) {
          setError('El código no es correcto. Verifícalo o solicita uno nuevo.');
        } else {
          setError(message || 'No pudimos verificar el código. Inténtalo de nuevo.');
        }
      } finally {
        setVerifying(false);
      }
    },
    [navigate],
  );

  // Verify straight away when the user arrived from the email link.
  useEffect(() => {
    if (autoSubmitted.current) return;
    if (queryEmail && queryCode) {
      autoSubmitted.current = true;
      verify(queryEmail, queryCode);
    }
  }, [queryEmail, queryCode, verify]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    verify(email, code);
  };

  const handleResend = async () => {
    setError('');
    setNotice('');

    if (!email.trim()) {
      setError('Ingresa tu correo para reenviarte el código.');
      return;
    }

    setResending(true);
    try {
      await authService.resendVerification({ email: email.trim() });
      setNotice(`Te enviamos un código nuevo a ${email.trim()}. Puede tardar un par de minutos.`);
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(err?.message || 'No pudimos reenviar el código. Inténtalo en unos minutos.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.primary.main }}>
              IQMaxx
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <MarkEmailReadRoundedIcon sx={{ fontSize: 44, color: colors.primary.main }} />
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', mb: 1 }}>
            Confirma tu correo
          </Typography>

          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 1 }}
          >
            {email ? (
              <>
                Enviamos un código de 6 dígitos a <strong>{email}</strong>.
              </>
            ) : (
              <>Enviamos un código de 6 dígitos a tu correo.</>
            )}
          </Typography>

          {/* The tester's mail landed in spam — say so before they go looking. */}
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 3, fontSize: '0.82rem' }}
          >
            Si no lo ves en unos minutos, revisa tu carpeta de spam o correo no deseado.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {error}
            </Alert>
          )}
          {notice && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {notice}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {/* Shown only when we don't already know the address, so the common
                path (arriving from Register) stays a single field. */}
            {!stateEmail && (
              <TextField
                label="Correo electrónico"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete="email"
              />
            )}

            <TextField
              label="Código de verificación"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              fullWidth
              size="small"
              sx={{ mb: 3 }}
              autoComplete="one-time-code"
              inputMode="numeric"
              autoFocus
              inputProps={{
                maxLength: 6,
                style: { letterSpacing: '0.5em', fontWeight: 700, fontSize: '1.1rem' },
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={verifying}
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 16px rgba(74,144,226,0.35)',
                '&:hover': { bgcolor: colors.primary.dark },
              }}
            >
              {verifying ? (
                <CircularProgress size={22} sx={{ color: 'white' }} />
              ) : (
                'Verificar mi cuenta'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 2.5 }}>
            <Button
              onClick={handleResend}
              disabled={resending || cooldown > 0}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: colors.primary.main,
                fontSize: '0.85rem',
              }}
            >
              {resending
                ? 'Enviando…'
                : cooldown > 0
                ? `Reenviar código en ${cooldown}s`
                : 'Reenviar código'}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 1.5 }}>
            <Box
              component={Link}
              to="/login"
              sx={{
                color: colors.text.secondary,
                fontSize: '0.8rem',
                textDecoration: 'none',
                '&:hover': { color: colors.primary.main },
              }}
            >
              ← Volver a iniciar sesión
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmail;
